// filtri/potenza.ts — v046: quanto è potente QUESTO telefono e quali filtri gli vanno bene (richiesta del proprietario:
// «alcuni filtri si muovono lenti nei cellulari vecchi… mettiamo il filtro consigliato in base al cellulare», senza
// mai dire niente di negativo a chi gioca).
//
// - Il «peso» di ogni filtro (leggero / medio / pesante) è nell'indice (indice.json, campo `peso`), misurato con
//   banco/pesi.mjs (docs/filtri/pesi-filtri.json). Chi usa il motore lo passa qui con impostaPesi().
// - Il telefono si ricorda sul dispositivo: alla fine di ogni partita con un filtro si salva il livello a cui si è
//   fermata la qualità automatica (qualita.ts); finché non c'è, valgono gli indizi di livelloIniziale (processori,
//   memoria, analisi sulla CPU). Un telefono è «lento» se si ferma in media dal livello 2 in giù (20 fps o meno, metà
//   delle analisi) o se una volta non ce l'ha fatta nemmeno al livello più basso.
// - Telefono lento: i filtri pesanti non sono «adatti» (non si propongono nei cambi automatici; se lo sceglie chi
//   gioca, si usa, ma se il telefono va in affanno si passa da soli a un filtro leggero simile: filtroFluidoSimile).
// Puro a parte localStorage (protetto): si prova con node:test.
import { LIVELLI, livelloIniziale } from "./qualita.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";

export type PesoFiltro = "leggero" | "medio" | "pesante";

/** Una voce dell'indice dei filtri (indice/indice.json, generato da scripts/genera-indice-filtri.mjs). */
export interface VoceIndice {
  id: string;
  numero: number;
  cartella: string;
  categoria: string;
  tecniche: string[];
  icona: boolean;
  /** v046: costo misurato (banco/pesi.mjs); manca per i filtri non ancora misurati (= "medio") */
  peso?: PesoFiltro;
}

/** Chiave delle preferenze sul telefono (JSON di MemoriaPotenza). */
export const CHIAVE_POTENZA = "ld.potenza_filtri";

export interface MemoriaPotenza {
  /** media dei livelli di qualità a fine partita (0 = massimo … LIVELLI.length-1 = minimo) */
  livello: number;
  /** partite contate */
  partite: number;
  /** una volta non ce l'ha fatta neanche al livello più basso */
  affanno?: boolean;
}

/** Livello medio da cui il telefono è «lento». */
export const LIVELLO_LENTO = 2;
/** Misure minime della qualità automatica perché il livello di fine partita conti (~15 s di filtro). */
export const MISURE_MINIME = 300;

/**
 * v046: filtri di benvenuto per chi gioca la prima volta (non ha mai scelto un filtro): piano base (gratis), leggeri,
 * molto buffi, che non coprono il viso (il riconoscimento della risata resta come senza filtro).
 */
export const FILTRI_BENVENUTO: readonly string[] = [
  "occhioni",
  "nasone",
  "occhiolino-stella",
  "mosca-molesta",
  "arcobaleno-sorriso",
  "pensierino",
  "testa-pentola",
  "bacio-volante",
];

const pesi = new Map<string, PesoFiltro>();
const categorie = new Map<string, string>();
let indizi: Parameters<typeof livelloIniziale>[0] | null = null;

/** Pesi e categorie dall'indice dei filtri (si chiama quando l'indice è caricato; si può richiamare). */
export function impostaPesi(voci: readonly Pick<VoceIndice, "id" | "categoria" | "peso">[]): void {
  for (const v of voci) {
    if (v.peso) pesi.set(v.id, v.peso);
    categorie.set(v.id, v.categoria);
  }
}

/** Il peso di un filtro (null = non si sa: vale come «medio»). */
export function pesoFiltro(id: string): PesoFiltro | null {
  return pesi.get(id) ?? null;
}

/** Gli indizi del telefono per la prima partita (quelli che il motore usa per livelloIniziale). */
export function impostaIndiziTelefono(i: Parameters<typeof livelloIniziale>[0]): void {
  indizi = i;
}

function indiziPredefiniti(): Parameters<typeof livelloIniziale>[0] {
  if (typeof navigator === "undefined") return {};
  return { processori: navigator.hardwareConcurrency, memoriaGb: (navigator as Navigator & { deviceMemory?: number }).deviceMemory };
}

export function leggiMemoria(): MemoriaPotenza | null {
  const testo = leggiPreferenza(CHIAVE_POTENZA);
  if (!testo) return null;
  try {
    const m = JSON.parse(testo) as Partial<MemoriaPotenza>;
    if (typeof m.livello !== "number" || !Number.isFinite(m.livello) || typeof m.partite !== "number") return null;
    return { livello: Math.max(0, Math.min(LIVELLI.length - 1, m.livello)), partite: m.partite, affanno: m.affanno === true };
  } catch {
    return null;
  }
}

function salvaMemoria(m: MemoriaPotenza): void {
  salvaPreferenza(CHIAVE_POTENZA, JSON.stringify(m));
}

/**
 * Fine di una partita con un filtro: il livello a cui si è fermata la qualità automatica. Conta solo se la qualità
 * ha misurato abbastanza (`misure`, vedi QualitaAutomatica.misure). La media pesa di più le partite recenti
 * (il telefono può cambiare: aggiornamenti, batteria).
 */
export function registraLivelloPartita(livello: number, misure: number): void {
  if (misure < MISURE_MINIME || !Number.isFinite(livello)) return;
  const l = Math.max(0, Math.min(LIVELLI.length - 1, livello));
  const m = leggiMemoria();
  salvaMemoria(m ? { ...m, livello: m.livello * 0.6 + l * 0.4, partite: m.partite + 1 } : { livello: l, partite: 1 });
}

/** Il telefono non ce l'ha fatta neanche al livello più basso con un filtro pesante: da ora è «lento». */
export function segnaAffanno(): void {
  const m = leggiMemoria();
  salvaMemoria({ livello: m?.livello ?? LIVELLI.length - 1, partite: m?.partite ?? 0, affanno: true });
}

/** true = meglio i filtri leggeri e medi (dalla memoria delle partite; la prima volta, dagli indizi del telefono). */
export function telefonoLento(): boolean {
  const m = leggiMemoria();
  if (m) return m.affanno === true || m.livello >= LIVELLO_LENTO;
  return livelloIniziale(indizi ?? indiziPredefiniti()) >= LIVELLO_LENTO;
}

/** Un filtro va bene su questo telefono? (pesante su telefono lento = no) */
export function filtroAdatto(id: string): boolean {
  return !(pesi.get(id) === "pesante" && telefonoLento());
}

/** I filtri adatti a questo telefono, nello stesso ordine (se nessuno lo è, tutti: meglio un filtro che niente). */
export function filtriConsigliati(ids: readonly string[]): string[] {
  if (!telefonoLento()) return [...ids];
  const ok = ids.filter((id) => pesi.get(id) !== "pesante");
  return ok.length ? ok : [...ids];
}

/**
 * Un filtro leggero al posto di `id` (che fa faticare il telefono): della stessa categoria se c'è, se no uno
 * qualsiasi leggero, tra quelli che chi gioca può usare. Sempre lo stesso per lo stesso filtro (niente sorprese a
 * ogni partita): si sceglie con un numero ricavato dall'id. null = nessun filtro leggero disponibile.
 */
export function filtroFluidoSimile(id: string, usabili: readonly string[]): string | null {
  const leggeri = usabili.filter((x) => x !== id && pesi.get(x) === "leggero");
  if (!leggeri.length) return null;
  const cat = categorie.get(id);
  const simili = cat ? leggeri.filter((x) => categorie.get(x) === cat) : [];
  const tra = simili.length ? simili : leggeri;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return tra[h % tra.length]!;
}

/**
 * Si passa da soli a un filtro più leggero? Solo se il filtro è pesante e il telefono è in affanno al livello più
 * basso della qualità automatica (il motore lo dice con suAffanno, dopo AFFANNO_DOPO_MS). Un filtro medio o leggero
 * non si cambia: lì la fatica è dell'analisi del volto, cambiare filtro non servirebbe.
 */
export function serveFiltroFluido(id: string | null, livello: number): boolean {
  return id !== null && pesi.get(id) === "pesante" && livello >= LIVELLI.length - 1;
}

/**
 * Il filtro da mettere in automatico a chi gioca la prima volta (non ha mai scelto un filtro): uno dei
 * FILTRI_BENVENUTO tra quelli usabili, a caso; se nessuno lo è, un filtro leggero usabile. null = nessuno.
 */
export function filtroDiBenvenuto(usabili: readonly string[], caso: () => number = Math.random): string | null {
  const ok = new Set(usabili);
  const scelti = FILTRI_BENVENUTO.filter((id) => ok.has(id) && pesi.get(id) !== "pesante");
  const tra = scelti.length ? scelti : usabili.filter((id) => pesi.get(id) === "leggero");
  if (!tra.length) return null;
  return tra[Math.min(tra.length - 1, Math.floor(caso() * tra.length))]!;
}

/** Solo per le prove: dimentica pesi, categorie e indizi. */
export function azzeraPotenzaPerProve(): void {
  pesi.clear();
  categorie.clear();
  indizi = null;
}
